import { AppConstants } from "./app.constants";

/**
 * Utility class for generic helper functions.
 */
export class GenericUtils {
    
    /**
     * Generates a random UUID (v4).
     * @returns A string representation of a UUID.
     */
    public static uuidv4(): string {    // https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /** @internal  */
    private static _normalize_map: Map<string, string> = new Map();

    /**
     * Normalizes a string to be used as a variable.
     * @param str - The string to be normalized.
     * @returns The normalized string or null if no string is provided.
    */
    public static Normalize(str: string | null) : string | null {
        if (str){
            let _str = this._normalize_map.get(str);
            if (!_str){
                _str = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s/g, "_");
                this._normalize_map.set(str, _str);
            }
            return _str;
        }

        return null;    // no string provided
    }

    /**
     * Enables advanced logging on the console.
     * @returns Returns the hardcoded value for verbose logging.
     */
    public static VerboseLogging(level: number): boolean {
        return level < 3;
    }

    /**
     * Delays refresh to be perfomed every clock tick
     * @returns Returns the hardcoded value for delay refresh (true for production / expected behaviour).
     */
    public static DelayedRefresh(): boolean {
        return true;    // production value should be true
    }

    /********************************/
    /* CRC32 CALCULATION            */
    /********************************/

    private static _global_crc32table: Array <number> | null = null;
    private static _global_crc8table: Uint8Array | null = null;

    /**
     * Calculates the CRC32 checksum of a given string.
     * This checksum is commonly used for error-checking in data transmission.
     * @param str - The input string to calculate the CRC32 checksum for. If null, it defaults to an empty string.
     * @returns The calculated CRC32 checksum as an unsigned integer.
     */
    public static CRC32(str: string | null): number{

        // https://stackoverflow.com/questions/18638900/javascript-crc32
        if (!str){
            str = '';
        }

        // build the CRC lookup table
        if (!this._global_crc32table){
            this._global_crc32table = [];
            for(let n=0; n < 256; n++) {
                let c = n;
                for(let k=0; k < 8; k++) {
                    c = ((c&1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
                }
                this._global_crc32table[n] = c;
            }
        }

        // calculate the CRC value
        let crc = 0 ^ (-1);
        for (let i=0; i < str.length; i++ ) {
            crc = (crc >>> 8) ^ this._global_crc32table[(crc ^ str.charCodeAt(i)) & 0xFF];
        }

        return (crc ^ (-1)) >>> 0;        
    }

    /**
     * Calculates an 8-bit CRC checksum and returns it as a 3-digit string.
     * @param str - Input string encoded as UTF-8 bytes. If null, it defaults to an empty string.
     * @returns CRC8 checksum as a zero-padded 3-digit string.
     */
    public static CRC8(str: string | null): string {
        if (!str){
            str = '';
        }

        if (!this._global_crc8table){
            this._global_crc8table = new Uint8Array([
                0x00, 0x07, 0x0E, 0x09, 0x1C, 0x1B, 0x12, 0x15,
                0x38, 0x3F, 0x36, 0x31, 0x24, 0x23, 0x2A, 0x2D,
                0x70, 0x77, 0x7E, 0x79, 0x6C, 0x6B, 0x62, 0x65,
                0x48, 0x4F, 0x46, 0x41, 0x54, 0x53, 0x5A, 0x5D,
                0xE0, 0xE7, 0xEE, 0xE9, 0xFC, 0xFB, 0xF2, 0xF5,
                0xD8, 0xDF, 0xD6, 0xD1, 0xC4, 0xC3, 0xCA, 0xCD,
                0x90, 0x97, 0x9E, 0x99, 0x8C, 0x8B, 0x82, 0x85,
                0xA8, 0xAF, 0xA6, 0xA1, 0xB4, 0xB3, 0xBA, 0xBD,
                0xC7, 0xC0, 0xC9, 0xCE, 0xDB, 0xDC, 0xD5, 0xD2,
                0xFF, 0xF8, 0xF1, 0xF6, 0xE3, 0xE4, 0xED, 0xEA,
                0xB7, 0xB0, 0xB9, 0xBE, 0xAB, 0xAC, 0xA5, 0xA2,
                0x8F, 0x88, 0x81, 0x86, 0x93, 0x94, 0x9D, 0x9A,
                0x27, 0x20, 0x29, 0x2E, 0x3B, 0x3C, 0x35, 0x32,
                0x1F, 0x18, 0x11, 0x16, 0x03, 0x04, 0x0D, 0x0A,
                0x57, 0x50, 0x59, 0x5E, 0x4B, 0x4C, 0x45, 0x42,
                0x6F, 0x68, 0x61, 0x66, 0x73, 0x74, 0x7D, 0x7A,
                0x89, 0x8E, 0x87, 0x80, 0x95, 0x92, 0x9B, 0x9C,
                0xB1, 0xB6, 0xBF, 0xB8, 0xAD, 0xAA, 0xA3, 0xA4,
                0xF9, 0xFE, 0xF7, 0xF0, 0xE5, 0xE2, 0xEB, 0xEC,
                0xC1, 0xC6, 0xCF, 0xC8, 0xDD, 0xDA, 0xD3, 0xD4,
                0x69, 0x6E, 0x67, 0x60, 0x75, 0x72, 0x7B, 0x7C,
                0x51, 0x56, 0x5F, 0x58, 0x4D, 0x4A, 0x43, 0x44,
                0x19, 0x1E, 0x17, 0x10, 0x05, 0x02, 0x0B, 0x0C,
                0x21, 0x26, 0x2F, 0x28, 0x3D, 0x3A, 0x33, 0x34,
                0x4E, 0x49, 0x40, 0x47, 0x52, 0x55, 0x5C, 0x5B,
                0x76, 0x71, 0x78, 0x7F, 0x6A, 0x6D, 0x64, 0x63,
                0x3E, 0x39, 0x30, 0x37, 0x22, 0x25, 0x2C, 0x2B,
                0x06, 0x01, 0x08, 0x0F, 0x1A, 0x1D, 0x14, 0x13,
                0xAE, 0xA9, 0xA0, 0xA7, 0xB2, 0xB5, 0xBC, 0xBB,
                0x96, 0x91, 0x98, 0x9F, 0x8A, 0x8D, 0x84, 0x83,
                0xDE, 0xD9, 0xD0, 0xD7, 0xC2, 0xC5, 0xCC, 0xCB,
                0xE6, 0xE1, 0xE8, 0xEF, 0xFA, 0xFD, 0xF4, 0xF3,
            ]);
        }

        const _data = new TextEncoder().encode(str);
        let _crc = 0;
        for (let _idx = 0; _idx < _data.length; _idx++){
            _crc = this._global_crc8table[(_crc ^ _data[_idx]) & 0xFF];
        }
        return (`000${_crc & 0xFF}`).slice(-3);
    }
    
    /********************************/
    /* DEEP OBJECT COPY             */
    /********************************/

    /**
     * Creates a deep copy of the given object.
     * Only plain objects, arrays, primitives, and {@link Date} are supported.
     * @param obj - The object to be copied.
     * @returns A deep copy of the object.
     */   
    public static CopyObject(obj: any): any {
        return this._copyObject(obj, new WeakSet());
    }

    /** @internal Recursive deep copy with circular-reference tracking. */
    private static _copyObject(obj: any, seen: WeakSet<object>): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        const _type = typeof obj;
        if (_type === 'function' || _type === 'symbol') {
            throw new Error(`Cannot copy unsupported type '${_type}'`);
        }

        if (_type !== 'object') {
            return obj;
        }

        if (obj instanceof Date) {
            return new Date(obj.getTime());
        }

        const _prototype = Array.isArray(obj) ? null : Object.getPrototypeOf(obj);
        if ((_prototype !== null) && (_prototype !== Object.prototype)) {
            throw new Error('Cannot copy object with non-plain prototype');
        }

        if (seen.has(obj)) {
            throw new Error('Circular reference in copy object operation');
        }
        seen.add(obj);

        const _cloned: any = Array.isArray(obj) ? [] : {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                _cloned[key] = this._copyObject(obj[key], seen);
            }
        }

        return _cloned;
    }

    /********************************/
    /* SIMPLE (QUICK) HASH          */
    /********************************/

    /**
     * Generates a simple hash of a given string.
     * @param str - The string to hash.
     * @returns The hashed string.
    */   
    public static simpleHash(str: string){
        const rev = str.split('').reverse().join('');

        function _hash(str: string) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = (hash << 5) - hash + char;
            }
            return (hash >>> 0).toString(36).padStart(7, '0');        
        }

        return _hash(str) + _hash(rev);
    }

    /********************************/
    /* ARRAY BUFFER SERIALIZATION   */
    /********************************/
   
    /**
     * Converts an image URL to a base64 string.
     * @param url - The image URL.
     * @param format - The format of the image (default is 'image/png').
     * @param resize - The resize factor (default is 1.0).
     * @param quality - The quality factor (default is 0.8).
     * @returns A promise that resolves to the base64 string of the image.
     */   
    public static imageTobase64(url: string, format='image/png', resize=1.0, quality=0.8) {
        return new Promise <string> ((resolve, reject) =>{
            const _img = new Image();
            _img.onload = () => {
                const _cnv: any = document.createElement("canvas");
                const _ctx: any = _cnv.getContext("2d");

                _cnv.width = _img.naturalWidth * resize;
                _cnv.height = _img.naturalHeight * resize;
                _ctx.drawImage(_img, 0, 0, _cnv.width, _cnv.height);

                resolve(_cnv.toDataURL(format, quality));
            }
            _img.onerror = () => {
                reject(new Error(`Failed to load image from URL: ${url}`));
            };

            _img.crossOrigin = "Anonymous";
            _img.src = url;
        });
    }

    /**
     * Converts a Uint8Array to a base64 string.
     * @param bytes - The Uint8Array to convert.
     * @returns The base64 encoded string.
     */   
    public static uint8ArrayToBase64(bytes: Uint8Array): string {
        const length = bytes.byteLength;

        let binary = '';
        for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }
   
    /**
     * Converts a base64 string to a Uint8Array.
     * @param base64 - The base64 encoded string.
     * @returns The resulting Uint8Array.
     */   
    public static base64ToUint8Array(base64: string): Uint8Array {
        const _normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
        const _remainder = _normalized.length % 4;
        const _padded = _normalized + (_remainder > 0 ? '='.repeat(4 - _remainder) : '');
        const binary_string = window.atob(_padded);
        const len = binary_string.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes;
    }

    /**
     * Converts an ArrayBuffer to a base64 string.
     * @param buffer - The ArrayBuffer to convert.
     * @returns The base64 encoded string.
     */   
    public static arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;

        let binary = '';
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    /**
     * Converts a base64 string to an ArrayBuffer.
     * @param base64 - The base64 encoded string.
     * @returns The resulting ArrayBuffer.
     */   
    public static base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binary_string = window.atob(base64);
        const len = binary_string.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Converts a string to a base64 encoded string.
     * @param str - The string to convert.
     * @returns The base64 encoded string or null if conversion fails.
     */   
    public static stringToBase64(str: string): string | null {
        const _arraybuffer = new TextEncoder().encode(str).buffer;
        if (_arraybuffer){
            return GenericUtils.arrayBufferToBase64(_arraybuffer);
        }
        return null;
    }

    /**
     * Converts a base64 string back to a regular string.
     * @param base64 - The base64 encoded string.
     * @returns The decoded string or null if conversion fails.
     */   
    public static base64ToString(base64: string): string | null {
        const _arraybuffer = GenericUtils.base64ToArrayBuffer(base64);
        if (_arraybuffer){
            return new TextDecoder().decode(_arraybuffer);
        }
        return null;
    }

    /********************************/
    /* MYSQL DATA CONVERSION        */
    /********************************/    

    /**
     * Converts a MySQL datetime string to an ISO 8601 date string.
     * This method replaces the space in a MySQL datetime string with a `T` 
     * and appends `.000Z` to convert it to UTC format.
     * 
     * @param date - The MySQL datetime string to convert. Example: `"2024-12-19 12:34:56"`.
     * @returns The ISO 8601 date string in UTC format, or `null` if the input is `null` or empty.
     */    
    public static mysqlToDateStr(date: string | null): string | null {
        return date ? date.replace(' ', 'T') + '.000Z' : null;
    }    

    /**
     * Converts a `Date` object to a MySQL datetime string.
     * This method formats the date in the MySQL `YYYY-MM-DD HH:MM:SS` format in UTC.
     * 
     * @param date - The `Date` object to convert.
     * @returns The formatted MySQL datetime string in UTC.
     */    
    public static dateStrToMysql(date: Date | null | undefined) : string | null{
        if (date){
            let _mysqldate = '';

            _mysqldate += date.getUTCFullYear() + '-';
            _mysqldate += ('00' + (date.getUTCMonth()+1)).slice(-2) + '-';
            _mysqldate += ('00' + date.getUTCDate()).slice(-2) + ' ';
            _mysqldate += ('00' + date.getUTCHours()).slice(-2) + ':'; 
            _mysqldate += ('00' + date.getUTCMinutes()).slice(-2) + ':'; 
            _mysqldate += ('00' + date.getUTCSeconds()).slice(-2);        
    
            return _mysqldate;    
        }

        return null;
    }

    /**
     * Extracts the file name from a file path for MySQL storage.
     * This method removes the directory path, leaving only the file name.
     * 
     * @param file - The file path to process. Example: `"/path/to/file.jpg"`.
     * @returns The extracted file name, or `null` if the input is `null` or empty.
     */    
    public static uploadToMysql(file: string | null){
        return file ? file.substring(file.lastIndexOf('/') + 1) : null;
    }

    /**
     * Converts a MySQL file reference to a full upload URL.
     * This method constructs a URL using a base URL, upload path, and the provided file name.
     * 
     * @param file - The file name stored in MySQL. Example: `"file.jpg"`.
     * @returns The full upload URL, or `null` if the input is `null` or empty.
     *          Example: `"https://example.com/uploads/file.jpg"`.
     */    
    public static mysqlToUpload(file: string | null){
        return file ? (AppConstants.baseURL + AppConstants.uploadPath + file) : null;
    }    
}

/**
 * Utility class for validating Spanish Tax Identifiers such as DNI and CIF.
 */
export class TaxIdValidators {

    /**
     * Formats a tax ID string.
     * @param taxid - The tax ID to format.
     * @returns A formatted tax ID string.
     */    
    public static ToTaxId(taxid: string) : string | null {
        if (!taxid) {
            return null;
        }

        let _taxid = taxid;
        if (taxid){
            _taxid = _taxid.toUpperCase().replace(/[^A-Z0-9]/g, '');
        }
        return _taxid;    
    }

    /*************************/
    /* DNI/CIF VALIDATORS    */
    /*************************/
    
    // https://www.lawebdelprogramador.com/codigo/JavaScript/1992-Validar-un-CIF-NIF-y-DNI.html

    /**
     * @internal
     * DNI: 8 digits + control letter (9 chars). NIE (via {@link _ValidateCIF}): 7 or 8 digit body + letter (8–9 chars).
     */
    private static _ValidateDNI(dni: string): boolean {
        if (!dni || (dni.length !== 8 && dni.length !== 9)) {
            return false;
        }

        const body = dni.substring(0, dni.length - 1);
        const letra = dni.substring(dni.length - 1).toUpperCase();

        if (dni.length === 9) {
            if (!/^[0-9]{8}[A-Z]$/.test(dni)) {
                return false;
            }
        } else if (!/^[0-9]{7}[A-Z]$/.test(dni)) {
            return false;
        }

        if (/^0+$/.test(body)) {
            return false;
        }

        const lockup = 'TRWAGMYFPDXBNJZSQVHLCKE';
        return lockup.charAt(Number(body) % 23) === letra;
    }

    /**
     * @internal
     */    
    private static _ValidateCIF_type1(cif: string): boolean {
        const letters: string[] = ['J', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
        const digits: string = cif.substring(1, cif.length - 1);
        const letter: string = cif.substring(0, 1);
        const control: string = cif.substring(cif.length - 1);
        let sum = 0;
        let i: number;
        let digit: number;
   
        if (!letter.match(/[A-Z]/)) {
            return false;
        }
   
        for (i = 0; i < digits.length; ++i) {
            digit = parseInt(digits[i]);
   
            if (isNaN(digit)) {
                return false;
            }
   
            if (i % 2 === 0) {
                digit *= 2;
                if (digit > 9) {
                    digit = Math.floor(digit / 10) + (digit % 10);
                }
   
                sum += digit;
            } 
            else {
                sum += digit;
            }
        }
   
        sum %= 10;
        if (sum !== 0) {
            digit = 10 - sum;
        } 
        else {
            digit = sum;
        }
   
        if (letter.match(/[ABEH]/)) {
            return String(digit) === control;
        }
        if (letter.match(/[NPQRSW]/)) {
            return letters[digit] === control;
        }
   
        return String(digit) === control || letters[digit] === control;
    }

    /**
     * @internal
     */
    private static _ValidateCIF_type2(cif: string): boolean {
        const valueCif = cif.substring(1, cif.length - 1);
    
        let suma = 0;
        for (let i = 1; i < valueCif.length; i += 2) {
            suma += parseInt(valueCif.substring(i, i + 1));
        }
    
        let suma2 = 0;
        for (let i = 0; i < valueCif.length; i += 2) {
            const result = parseInt(valueCif.substring(i, i + 1)) * 2;
            if (String(result).length === 1) {
                suma2 += result;
            } else {
                suma2 += parseInt(String(result).substring(0, 1)) + parseInt(String(result).substring(1, 2));
            }
        }
    
        suma += suma2;
    
        let unidad = parseInt(String(suma).substring(1, 2));
        unidad = 10 - unidad;
    
        if (unidad === 10) {
            unidad = 0;
        }
    
        return (cif.substring(cif.length - 1) === String(unidad));
    }    

    /**
     * @internal
     */
    private static _ValidateCIF(cif: string): boolean {
        if (!cif || cif.length !== 9) {
            return false;
        }
    
        const primerCaracter = cif.substring(0, 1);
        if (primerCaracter.match(/^[XYZ]$/)) {
            let newcif = "";
    
            if (primerCaracter === "X") {
                newcif = cif.substring(1);
            }
    
            if (primerCaracter === "Y") {
                newcif = "1" + cif.substring(1);
            }
    
            if (primerCaracter === "Z") {
                newcif = "2" + cif.substring(1);
            }
    
            return this._ValidateDNI(newcif);
        }
    
        return this._ValidateCIF_type1(cif) || this._ValidateCIF_type2(cif) || this._ValidateDNI(cif);
    }
    
    /**
     * Validates a generic Spanish tax ID (DNI or CIF).
     * @param value - The tax ID to validate.
     * @returns True if the tax ID is valid, otherwise false.
     */    
    public static ValidateTaxId(value: string | null): boolean{
        if (!value){
            return false;
        }

        const _taxid = this.ToTaxId(value);
        if (!_taxid){
            return false;
        }

        return this._ValidateCIF(_taxid);
    }

    /**
     * Spanish NIF that may carry a 0% VAT rate: valid control character and
     * first letter R, G, Q, N or W. A foreign tax id is not a Spanish NIF.
     */
    public static IsZeroRate(taxId: string | null | undefined, country?: string | null): boolean {
        const _country = String(country || '').trim().toUpperCase();
        if (_country && !CountryCodes.Is_ES(_country)){
            return false;
        }

        if (!this.ValidateTaxId(taxId ?? null)){
            return false;
        }

        const _nif = this.ToTaxId(String(taxId || '')) || '';
        return /^[RGQNW]/.test(_nif);
    }
}

/**
 * Class for managing global mutex locks.
 * 
 * ### Example
 * ```typescript
 * async function exampleUsage() {
 *     // Create an instance of GlobalMutex
 *     const mutex = new GlobalMutex();
 * 
 *     // Define a function that requires exclusive access
 *     async function criticalSection(key: string) {
 *         console.log(`Attempting to lock the key: ${key}`);
 *         
 *         // Lock the key
 *         await mutex.lock(key);
 *         console.log(`Key locked: ${key}`);
 *         
 *         // Critical section
 *         console.log(`Executing critical section for key: ${key}`);
 *         await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulates async task
 * 
 *         // Unlock the key
 *         mutex.unlock(key);
 *         console.log(`Key unlocked: ${key}`);
 *     }
 * 
 *     // Run multiple calls with the same key
 *     criticalSection("resource1");
 *     criticalSection("resource1");
 *     criticalSection("resource1");
 * 
 *     // Managing different keys simultaneously
 *     criticalSection("resource2");
 * }
 * 
 * // Call the example function
 * exampleUsage();
 * ```
 */
export class GlobalMutex {
    private _mutex: Map<string, { locked: boolean; queue: (() => void)[] }> = new Map();

    /**
     * @internal
     */    
    private _getMutex(key: string): { locked: boolean; queue: (() => void)[] } {
        if (!this._mutex.has(key)) {
            this._mutex.set(key, { locked: false, queue: [] });
        }
        return this._mutex.get(key) as { locked: boolean; queue: (() => void)[] };
    }

    /**
     * Locks the specified key, blocking further calls until the lock is released.
     * @param key - The key to lock.
     */    
    async lock(key: string) {
        const mutex = this._getMutex(key);
        await new Promise<void>((resolve) => {
            if (!mutex.locked) {
                mutex.locked = true;
                resolve();
            } 
            else {
                mutex.queue.push(resolve);
            }
        });
    }

    /**
     * Releases the lock on the specified key, allowing queued calls to proceed.
     * @param key - The key to unlock.
     */    
    unlock(key: string) {
        const mutex = this._getMutex(key);
        if (mutex.queue.length > 0) {
            const resolve = mutex.queue.shift();
            if (resolve) {
                resolve();
            } 
            else {
                mutex.locked = false;
            }
        } 
        else {
            mutex.locked = false;
        }
    }
}

/**
 * Represents a split of a price amount with its tax information
 */
type TaxesSplit = {
    /** The total amount including taxes */
    total: number, 
    /** The tax rate as a percentage (e.g., 21 for 21%) */
    rate: number, 
    /** The base amount without taxes */
    base: number, 
    /** The tax amount */
    taxes: number 
}

/**
 * Aggregated tax information for a price
 */
type TaxesInfo = {
    /** The total amount including all taxes */
    total: number,
    /** The sum of all base amounts without taxes */
    base: number,
    /** The sum of all tax amounts */
    taxes: number,
    /** Detailed breakdown of taxes by rate */
    split: Array<TaxesSplit>
}

/**
 * Comprehensive price information with tax details and formatting
 * 
 */
export type PriceType = {
    /** The total price value including taxes */
    val: number, 
    /** Indicates whether the price is negative */
    neg: boolean,
    /** The absolute value of the price (always positive) */
    abs: number,
    /** The integer part of the absolute price value */
    int: string,
    /** The decimal part of the absolute price value */
    dec: string,
    /** Formatted price string with comma as decimal separator (e.g., "123,45") */
    str: string,
    /** Detailed tax information including base amounts, tax amounts, and breakdown by tax rates */
    tax: TaxesInfo,
    /** Detailed discount information including base amounts, tax amounts, and breakdown by tax rates */
    dsc: TaxesInfo
}

/**
 * A class for handling price calculations with different tax rates
 * 
 * This class manages price components with their respective tax rates,
 * calculates tax amounts, and provides formatted price information.
 * It supports multiple discounts that are applied proportionally across
 * all tax groups based on their relative weight in the total.
 * 
 * @example
 * ```typescript
 * const price = new PriceInfo();
 * price.Add(100, 21); // Add 100€ with 21% tax rate
 * price.Add(50, 10);  // Add 50€ with 10% tax rate
 * price.Del(30);      // Add a 30€ discount
 * const priceInfo = price.price; // Get complete price information
 * console.log(`Total: ${priceInfo.val}€, Taxes: ${priceInfo.tax.taxes}€`);
 * // Output: Total: 120€, Taxes: ~19.83€
 * ```
 * 
 * @example Multiple discounts
 * ```typescript
 * const price = new PriceInfo();
 * price.Add(100, 21); // Add 100€ with 21% tax rate
 * price.Add(50, 10);  // Add 50€ with 10% tax rate
 * price.Del(30);      // First discount: 30€
 * price.Del(20);      // Second discount: 20€ (applied to remaining amount)
 * const priceInfo = price.price;
 * console.log(`Final total: ${priceInfo.val}€`);
 * // Discounts are applied proportionally to each tax group
 * ```
 */
export class PriceInfo {
    private _val = 0;       // total price value
    private _dsc = 0;       // total discount value

    private _taxmap = new Map <number, TaxesSplit> ();  // increments map
    private _dscmap = new Map <number, TaxesSplit> ();  // decrements map

    /**
     * Creates a new PriceInfo instance
     * 
     * @param total - Optional initial amount to add (including tax)
     * @param rate - Optional tax rate for the initial amount as a percentage (e.g., 21 for 21%)
     */    
    constructor(total = 0, rate = AppConstants.defaultTaxRate){
        this.Add(total, rate);
    }

    /**
     * Adds a price amount with a specific tax rate to the price
     * 
     * If multiple amounts are added with the same tax rate, they will be 
     * aggregated into a single tax group.
     * 
     * @param amount - The amount to add (including tax). Must be positive.
     * @param taxrate - The tax rate as a percentage (e.g., 21 for 21%). Must be non-negative.
     * 
     * @example
     * ```typescript
     * price.Add(100, 21);    // Add 100€ with 21% tax
     * price.Add(50, 21);     // Add another 50€ with same tax rate (will aggregate)
     * // Result: 150€ total with 21% tax rate
     * ```
     */     
    Add(amount: number, taxrate: number): void {
        this._val += amount;
        
        let _split: TaxesSplit | null = this._taxmap.get(taxrate) || null;
        if (!_split){
            _split = { total: 0, rate: taxrate, base: 0, taxes: 0 };
        }

        _split.total += amount;
        this._taxmap.set(taxrate, _split);
    }

    /**
     * Removes a gross amount from the total, distributed proportionally across all existing tax rates.
     * 
     * The removal amount is allocated to each tax group based on its relative weight in the total.
     * Both base and tax amounts are adjusted accordingly. The discount is applied when the 
     * price getter is called, ensuring all prices are set before calculating proportions.
     * 
     * @param taxmap - Array of tax splits (cloned from _taxmap) to apply discounts to
     * @param amount - The total amount to remove (including tax). Must be positive.
     * 
     * @remarks
     * This method modifies the provided taxmap array in-place and updates the internal
     * _dscmap with the discount amounts for each tax rate. Discounts are limited by
     * the available amount in each tax group (cannot exceed the group's total).
     * 
     * @internal This is a private method used internally by the price getter.
     */ 
    private discount(taxmap: TaxesSplit[], amount: number): void {
        if (amount === 0 || this._val === 0) {
            return;
        }

        let total = 0;
        for (const split of taxmap){
            total += split.total;
        }

        let _applied = 0;
        for (const split of taxmap){
            let disct: TaxesSplit | null = this._dscmap.get(split.rate) || null;
            if (!disct){
                disct = { total: 0, rate: split.rate, base: 0, taxes: 0 };
            }            

            const _toapply = Math.min(amount * (split.total / total), split.total);

            disct.total += _toapply;
            _applied += _toapply;            
            split.total -= _toapply;    // adjust the remaining price (in the copy)

            this._dscmap.set(split.rate, disct);
        }

        this._dsc += _applied;
    }   

    private _discounts: Array <number> = [];

    /**
     * Pushes a discount value to be applied on final price calculation
     * 
     * Discounts are not applied immediately but stored for calculation when
     * the price getter is accessed. This allows for proper proportional
     * distribution based on the final price composition.
     * 
     * Multiple discounts are applied sequentially, with each subsequent
     * discount applied to the remaining amount after previous discounts.
     * 
     * @param amount - The total amount to remove (including tax). Must be positive.
     * 
     * @example
     * ```typescript
     * price.Del(30);  // Queue first discount
     * price.Del(20);  // Queue second discount
     * const info = price.price; // Both discounts applied proportionally
     * ```
     */    
    Del(amount: number){
        this._discounts.push(amount);
    }

    /**
     * Gets comprehensive price information including tax calculations and formatting
     * 
     * This property calculates the final price by applying all queued discounts
     * proportionally across tax groups, then computes base amounts, tax amounts,
     * and formats the result.
     * 
     * Discounts are applied in the order they were added, with each discount
     * applied to the remaining amount after previous discounts.
     * 
     * @returns An object containing:
     *  - val: Total price value after discounts
     *  - neg: Flag indicating if the price is negative
     *  - abs: Absolute price value
     *  - int: Integer part of the absolute price
     *  - dec: Decimal part of the absolute price  
     *  - str: Formatted price as string with comma as decimal separator
     *  - tax: Detailed tax information for the final amount
     *  - dsc: Detailed discount information broken down by tax rate
     * 
     * @example
     * ```typescript
     * const price = new PriceInfo();
     * price.Add(121, 21);  // 100€ base + 21€ tax
     * const info = price.price;
     * console.log(info.val);           // 121
     * console.log(info.tax.base);      // ~100
     * console.log(info.tax.taxes);     // ~21
     * console.log(info.str);           // "121,00"
     * ```
     */   
    get price(): PriceType {
        const _taxes: TaxesInfo = { total: 0, base: 0, taxes: 0, split: [] };
        const _dscnt: TaxesInfo = { total: 0, base: 0, taxes: 0, split: [] };

        // clone the source taxmap so its not affected by discounts aplication
        const _taxmap: TaxesSplit[] = Array.from(this._taxmap.values()).map(split => ({
            total: split.total,
            rate: split.rate,
            base: split.base,
            taxes: split.taxes
        }));

        // calculate the discounts once all the prices are set
        this._dscmap.clear();
        for(const _disc of this._discounts){
            this.discount(_taxmap, _disc);
        }

        // calculate the final price information
        for(const split of this._taxmap.values()){
            split.base = split.total / (1 + (split.rate / 100));
            split.taxes = split.total - split.base;

            _taxes.split.push(split);

            _taxes.total += split.total;
            _taxes.base += split.base;
            _taxes.taxes += split.taxes;
        }

        // adjust the totals with the discounts
        for(const split of this._dscmap.values()){
            split.base = split.total / (1 + (split.rate / 100));
            split.taxes = split.total - split.base;

            _dscnt.split.push(split);
            
            _taxes.total -= split.total;
            _taxes.base -= split.base;
            _taxes.taxes -= split.taxes;
        }

        _taxes.total = Math.round(_taxes.total * 100) / 100;
        _taxes.base = Math.round(_taxes.base * 100) / 100;
        _taxes.taxes = Math.round(_taxes.taxes * 100) / 100;

        const _val = this._val - this._dsc;
        const _abs = Math.abs(_val);
        const [_int, _dec] = _abs.toFixed(2).split(".");

        return {
            val: _val,
            neg: (_val < 0),
            abs: _abs,
            str: _int + "," + _dec,
            int: _int,
            dec: _dec,
            tax: _taxes,
            dsc: _dscnt
        }
    }
}

/**
 * Converts between ISO 3166-1 alpha-2 country codes and English display names.
 */
export class CountryCodes {

    /**
     * ISO 3166-1 alpha-2 codes accepted by Verifactu (CountryType2 XSD).
     * @internal Source: `server/.../verifactu/xsd/SuministroInformacion.xsd`
     */
    /** EU member states (ISO 3166-1 alpha-2), post-Brexit list (excludes GB). */
    private static readonly _eu_member_codes: ReadonlySet<string> = new Set([
        'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
        'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
    ]);

    private static readonly _iso_country_codes: readonly string[] = [
        'AF', 'AL', 'DE', 'AD', 'AO', 'AI', 'AQ', 'AG', 'SA', 'DZ', 'AR', 'AM', 'AW', 'AU', 'AT', 'AZ',
        'BS', 'BH', 'BD', 'BB', 'BE', 'BZ', 'BJ', 'BM', 'BY', 'BO', 'BA', 'BW', 'BV', 'BR', 'BN', 'BG',
        'BF', 'BI', 'BT', 'CV', 'KY', 'KH', 'CM', 'CA', 'CF', 'CC', 'CO', 'KM', 'CG', 'CD', 'CK', 'KP',
        'KR', 'CI', 'CR', 'HR', 'CU', 'TD', 'CZ', 'CL', 'CN', 'CY', 'CW', 'DK', 'DM', 'DO', 'EC', 'EG',
        'AE', 'ER', 'SK', 'SI', 'ES', 'US', 'EE', 'ET', 'FO', 'PH', 'FI', 'FJ', 'FR', 'GA', 'GM', 'GE',
        'GS', 'GH', 'GI', 'GD', 'GR', 'GL', 'GU', 'GT', 'GG', 'GN', 'GQ', 'GW', 'GY', 'HT', 'HM', 'HN',
        'HK', 'HU', 'IN', 'ID', 'IR', 'IQ', 'IE', 'IM', 'IS', 'IL', 'IT', 'JM', 'JP', 'JE', 'JO', 'KZ',
        'KE', 'KG', 'KI', 'KW', 'LA', 'LS', 'LV', 'LB', 'LR', 'LY', 'LI', 'LT', 'LU', 'XG', 'MO', 'MK',
        'MG', 'MY', 'MW', 'MV', 'ML', 'MT', 'FK', 'MP', 'MA', 'MH', 'MU', 'MR', 'YT', 'UM', 'MX', 'FM',
        'MD', 'MC', 'MN', 'ME', 'MS', 'MZ', 'MM', 'NA', 'NR', 'CX', 'NP', 'NI', 'NE', 'NG', 'NU', 'NF',
        'NO', 'NC', 'NZ', 'IO', 'OM', 'NL', 'BQ', 'PK', 'PW', 'PA', 'PG', 'PY', 'PE', 'PN', 'PF', 'PL',
        'PT', 'PR', 'QA', 'GB', 'RW', 'RO', 'RU', 'RE', 'SB', 'SV', 'WS', 'AS', 'KN', 'SM', 'SX', 'PM',
        'VC', 'SH', 'LC', 'ST', 'SN', 'RS', 'SC', 'SL', 'SG', 'SY', 'SO', 'LK', 'SZ', 'ZA', 'SD', 'SS',
        'SE', 'CH', 'SR', 'TH', 'TW', 'TZ', 'TJ', 'PS', 'TF', 'TL', 'TG', 'TK', 'TO', 'TT', 'TN', 'TC',
        'TM', 'TR', 'TV', 'UA', 'UG', 'UY', 'UZ', 'VU', 'VA', 'VE', 'VN', 'VG', 'VI', 'WF', 'YE', 'DJ',
        'ZM', 'ZW', 'QU', 'XB', 'XU', 'XN',
    ];

    /** @internal */
    private static _code_to_name: Map<string, string> | null = null;

    /** @internal */
    private static _name_to_code: Map<string, string> | null = null;

    /** @internal */
    private static _display_names: Intl.DisplayNames | null | undefined = undefined;

    /** @internal */
    private static _displayNames(): Intl.DisplayNames | null {
        if (this._display_names !== undefined) {
            return this._display_names;
        }

        try {
            this._display_names = new Intl.DisplayNames(['en'], { type: 'region' });
        } catch {
            this._display_names = null;
        }

        return this._display_names;
    }

    /** @internal */
    private static _nameKey(name: string): string {
        return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    }

    /** @internal */
    private static _ensureMaps(): void {
        if (this._code_to_name) {
            return;
        }

        const _codeToName = new Map<string, string>();
        const _nameToCode = new Map<string, string>();
        const _display = this._displayNames();

        for (const _code of this._iso_country_codes) {
            const _upper = _code.toUpperCase();
            const _name = _display?.of(_upper)?.trim() || '';
            if (!_name || _name.toUpperCase() === _upper) {
                continue;
            }

            _codeToName.set(_upper, _name);

            const _key = this._nameKey(_name);
            if (_key && !_nameToCode.has(_key)) {
                _nameToCode.set(_key, _upper);
            }
        }

        this._code_to_name = _codeToName;
        this._name_to_code = _nameToCode;
    }

    /**
     * Converts an ISO 3166-1 alpha-2 country code to its English display name.
     * Names match {@link Intl.DisplayNames} locale `en`.
     *
     * @param code - Country code (e.g. `ES`, `fr`).
     * @returns English country name, or `null` if the code is empty or unknown.
     */
    public static Code2Name(code: string | null | undefined): string | null {
        const _code = String(code || '').trim().toUpperCase();
        if (!_code) {
            return null;
        }

        this._ensureMaps();
        const _codeToName = this._code_to_name;
        if (_codeToName == null){
            return null;
        }

        return _codeToName.get(_code) || null;
    }

    /**
     * Converts an English country name to its ISO 3166-1 alpha-2 code.
     * Lookup is case- and accent-insensitive.
     *
     * @param name - Country name (e.g. `Spain`, `france`).
     * @returns Country code, or `null` if the name is empty or unknown.
     */
    public static Name2Code(name: string | null | undefined): string | null {
        const _key = this._nameKey(String(name || ''));
        if (!_key) {
            return null;
        }

        this._ensureMaps();
        const _nameToCode = this._name_to_code;
        if (_nameToCode == null){
            return null;
        }

        return _nameToCode.get(_key) || null;
    }

    /**
     * @param code - ISO 3166-1 alpha-2 country code (e.g. `ES`, `fr`).
     */
    public static Is_ES(code: string | null | undefined): boolean {
        return String(code || '').trim().toUpperCase() === 'ES';
    }

    /**
     * @param code - ISO 3166-1 alpha-2 country code (e.g. `FR`, `US`).
     */
    public static Is_EU(code: string | null | undefined): boolean {
        const _code = String(code || '').trim().toUpperCase();
        return _code !== '' && this._eu_member_codes.has(_code);
    }
}